import { Color, LineLayer } from 'deck.gl';
import { MVTLayer, MVTLayerProps } from '@deck.gl/geo-layers';
import { ScatterplotLayer } from '@deck.gl/layers';
import { LayerManager } from '../LayerManager';

export interface MVTLayerConfig {
  id: string;
  layerUrl: string;
  minZoom?: number;
  maxZoom?: number;
  elevation?: number;
  lineColor?: Color;
  pointColor?: Color;
  lineWidth?: number;
  pointRadius?: number;
  visible?: boolean;
}

export class MVTLayerManager implements LayerManager {
  private config: MVTLayerConfig;
  private layer: MVTLayer<MVTLayerProps> | null = null;

  constructor(config: MVTLayerConfig) {
    this.config = {
      minZoom: 0,
      maxZoom: 14,
      elevation: 50,
      lineColor: [255, 200, 200, 200],
      pointColor: [255, 0, 0, 255],
      lineWidth: 10,
      pointRadius: 50,
      visible: true,
      ...config
    };
    this.createLayer();
  }

  private getLineFeatures(data: any, tile: any): any[] {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    
    const expandedLineFeatures: any[] = [];
    
    data.forEach((f: any) => {
      const isLineString = f?.geometry?.type === "LineString";
      const isMultiLineString = f?.geometry?.type === "MultiLineString";
      
      if (isLineString) {
        // Split LineString into individual segments
        const coordinates = f.geometry.coordinates;
        if (coordinates && coordinates.length >= 2) {
          for (let i = 0; i < coordinates.length - 1; i++) {
            const segmentFeature = {
              geometry: {
                type: "LineString",
                coordinates: [coordinates[i], coordinates[i + 1]]
              },
              properties: f.properties || {}
            };
            expandedLineFeatures.push(segmentFeature);
          }
        }
      } else if (isMultiLineString) {
        console.debug(`[MVTLayerManager] Processing MultiLineString with ${f.geometry.coordinates?.length || 0} line strings`);
        
        // Handle MultiLineString coordinate conversion
        const lineStringCoordinates = f.geometry.coordinates;
        if (lineStringCoordinates && Array.isArray(lineStringCoordinates)) {
          lineStringCoordinates.forEach((lineCoords: any[]) => {
            if (lineCoords && lineCoords.length >= 2) {
              // Split each LineString into segments
              for (let i = 0; i < lineCoords.length - 1; i++) {
                const segmentFeature = {
                  geometry: {
                    type: "LineString",
                    coordinates: [lineCoords[i], lineCoords[i + 1]]
                  },
                  properties: f.properties || {}
                };
                expandedLineFeatures.push(segmentFeature);
              }
            }
          });
        }
      }
    });
    
    console.log(`[MVTLayerManager] Expanded ${expandedLineFeatures.length} line segments from original features`, {
      originalFeatures: data.length,
      expandedSegments: expandedLineFeatures.length,
      sampleCoordinates: expandedLineFeatures.slice(0, 3).map(f => f.geometry.coordinates)
    });
    
    return expandedLineFeatures;
  }

  private getPointFeatures(data: any): any[] {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    
    return data.filter((f: any) => f?.geometry?.type === "Point");
  }

  private createLayer(): void {
    const mvtLayerProps = {
      id: this.config.id,
      data: this.config.layerUrl,
      minZoom: this.config.minZoom!,
      maxZoom: this.config.maxZoom!,
      
      // Styling properties
      getFillColor: (f: any): Color => [200, 200, 255, 180],
      getLineColor: (f: any): Color => [0, 0, 0, 255],
      getLineWidth: 1,
      lineWidthMinPixels: 1,
      
      // Elevation settings
      extruded: false,
      
      // Interaction
      pickable: true,
      visible: this.config.visible!,

      renderSubLayers: (props: any) => {
        const { id, tile } = props;
        const data = tile.dataInWGS84; // our pre-fetched and transformed data

        console.log(`[MVTLayerManager] Rendering sublayers for tile ${tile?.x}-${tile?.y}-${tile?.z}`, {
          hasData: !!data,
          dataLength: data?.length || 0
        });

        // Defensive: only render if tile has features
        if (!data) {
          console.warn(`[MVTLayerManager] No data available for tile ${tile?.x}-${tile?.y}-${tile?.z}`);
          return null;
        }

        const lineFeatures = this.getLineFeatures(data, tile);
        const pointFeatures = this.getPointFeatures(data);

        const layerElevation = this.config.elevation!;

        const sublayers = [
          new LineLayer({
            id: `${id}-lines-${tile.x}-${tile.y}-${tile.z}`,
            data: lineFeatures,
            getSourcePosition: (f) => {
              const coords = f?.geometry?.coordinates?.[0];
              return coords ? [coords[0], coords[1], layerElevation] : [0, 0, 0];
            },
            getTargetPosition: (f) => {
              const coords = f?.geometry?.coordinates?.[1];
              return coords ? [coords[0], coords[1], layerElevation] : [0, 0, 0];
            },
            getLineColor: this.config.lineColor!,
            getWidth: this.config.lineWidth!,
            pickable: true,
          }),
          new ScatterplotLayer({
            id: `${id}-points-${tile.x}-${tile.y}-${tile.z}`,
            data: pointFeatures,
            getPosition: (f) => {
              const coords = f?.geometry?.coordinates;
              return coords ? [coords[0], coords[1], layerElevation] : [0, 0, 0];
            },
            getLineColor: this.config.pointColor!,
            getFillColor: this.config.pointColor!,
            getRadius: this.config.pointRadius!,
            radiusMinPixels: 5,
            radiusMaxPixels: 20,
            pickable: true,
          }),
        ];

        console.log(`[MVTLayerManager] Created ${sublayers.length} sublayers for tile ${tile.x}-${tile.y}-${tile.z}`, {
          lineLayerFeatures: lineFeatures.length,
          pointLayerFeatures: pointFeatures.length,
        });

        return sublayers;
      },
    };

    this.layer = new MVTLayer<MVTLayerProps>(mvtLayerProps);
  }

  public getLayer(): MVTLayer<MVTLayerProps> | null {
    return this.layer;
  }

  public updateConfig(newConfig: Partial<MVTLayerConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.createLayer();
  }

  public setVisibility(visible: boolean): void {
    this.config.visible = visible;
    if (this.layer) {
      this.layer = this.layer.clone({ visible });
    }
  }

  public getId(): string {
    return this.config.id;
  }

  public getConfig(): MVTLayerConfig {
    return { ...this.config };
  }
}